import { DEFAULT_CAMERA_GIMBAL, type CameraGimbalId } from '../../../renderer/src/camera-gimbal';
import { DEFAULT_DEPTH_OF_FIELD, type DepthOfFieldSettings } from '../../../renderer/src/depth-of-field';
import type { CinematicSequence } from '../../../renderer/src/cinematic-sequence';
import type { MapStyleId } from '../../../renderer/src/map-style';
import { compileCameraPlan, type CameraViewport, type CameraPlan } from './camera-plan';
import type { CameraPlatformId } from './camera-platform';
import type { CameraDistanceId } from './camera-distance';
import type { RidePoint } from './ride';
import { cinematicTimeline } from './cinematic-timeline';
import type { NightHawkEffects } from './night-hawk-effects';
import { resolveCameraKeyframes } from './camera-keyframes';
import type { PresentationAppearance } from '../../../renderer/src/presentation-appearance';
import { cinematicTransitionAmount } from './cinematic-appearance';
import { manualCameraZoom } from './camera-zoom';
import { compilePropertyPlan } from './cinematic-property-plan';

export interface CinematicViewport extends CameraViewport {
  automaticPitchLimit?: number | undefined;
  deferMapZoom?: boolean | undefined;
}

export interface CinematicBase {
  cameraStyleVersion?: 3 | 4 | undefined;
  nightHawkEffects?: Partial<NightHawkEffects> | undefined;
  cameraGimbal?: CameraGimbalId | undefined;
  cameraStyle: CameraPlatformId;
  cameraDistance: CameraDistanceId;
  cameraAngle?: number | undefined;
  mapStyle: MapStyleId;
  depthOfField?: DepthOfFieldSettings | undefined;
  presentationAppearance?: PresentationAppearance | undefined;
}

export interface CinematicSettings {
  cameraGimbal: CameraGimbalId;
  cameraStyle: CameraPlatformId;
  cameraDistance: CameraDistanceId;
  cameraZoom?: number | null | undefined;
  cameraAngle: number | null;
  mapStyle: MapStyleId;
  depthOfField: DepthOfFieldSettings;
  presentationAppearance?: PresentationAppearance | undefined;
}

/** Compiles one seek-independent sequence; moving camera plans are reused between frames. */
export function compileCinematicPlan(points: readonly RidePoint[], base: CinematicBase,
  sequence: CinematicSequence | undefined, viewport: CinematicViewport, duration: number) {
  const timed = sequence ? cinematicTimeline(points, sequence, duration) : [];
  if (sequence?.cameraTracks) resolveCameraKeyframes(sequence.cameraTracks, duration);
  const initial: CinematicSettings = { cameraGimbal: base.cameraGimbal ?? DEFAULT_CAMERA_GIMBAL, cameraStyle: base.cameraStyle, cameraDistance: base.cameraDistance, cameraAngle: base.cameraAngle ?? null,
    mapStyle: base.mapStyle, depthOfField: base.depthOfField ?? { ...DEFAULT_DEPTH_OF_FIELD }, presentationAppearance: base.presentationAppearance ?? {} };
  const plans = new Map<string, CameraPlan>();
  const cameraFor = (settings: CinematicSettings): CameraPlan => {
    const key = `${settings.cameraStyle}:${settings.cameraDistance}`;
    let plan = plans.get(key);
    if (!plan) {
      const automatic = compileCameraPlan(points, settings.cameraStyle, viewport, duration, settings.cameraDistance, undefined, base.nightHawkEffects, base.cameraStyleVersion);
      const limit = settings.cameraStyle === 'night-hawk' && base.nightHawkEffects ? undefined : viewport.automaticPitchLimit;
      plan = limit === undefined ? automatic : { ...automatic, evaluate(progress) {
        const pose = automatic.evaluate(progress);
        return { ...pose, pitch: Math.min(pose.pitch, limit) };
      } };
      plans.set(key, plan);
    }
    if (settings.cameraAngle === null && (viewport.deferMapZoom || settings.cameraZoom == null)) return plan;
    const automatic = plan;
    const pitch = settings.cameraAngle;
    const zoom = viewport.deferMapZoom ? undefined : manualCameraZoom(settings.cameraZoom, settings.cameraDistance);
    return { ...automatic, evaluate: progress => ({ ...automatic.evaluate(progress),
      ...(pitch === null ? {} : { pitch }), ...(zoom === undefined ? {} : { zoom }) }) };
  };
  let previous = initial;
  const segments = timed.map(item => {
    const from = previous;
    const overrides = item.waypoint.overrides;
    const to: CinematicSettings = { cameraGimbal: overrides.cameraGimbal ?? from.cameraGimbal, cameraStyle: overrides.cameraStyle ?? from.cameraStyle,
      cameraDistance: overrides.cameraDistance ?? from.cameraDistance,
      cameraZoom: overrides.cameraZoom === undefined ? from.cameraZoom : overrides.cameraZoom,
      cameraAngle: overrides.cameraAngle === undefined ? from.cameraAngle : overrides.cameraAngle,
      mapStyle: overrides.mapStyle ?? from.mapStyle, depthOfField: overrides.depthOfField ?? from.depthOfField,
      presentationAppearance: { ...from.presentationAppearance, ...overrides.presentationAppearance } };
    previous = to;
    return { ...item, from, to, fromCamera: cameraFor(from), toCamera: cameraFor(to) };
  });
  const initialCamera = cameraFor(initial);
  const evaluateProperties = compilePropertyPlan(timed, duration, initial, cameraFor);
  return { waypoints: timed, initial, initialCamera, segments, evaluate(progress: number) {
    if (!Number.isFinite(progress)) throw new TypeError('Film progress must be finite.');
    const bounded = Math.max(0, Math.min(1, progress));
    const seconds = bounded * duration;
    const segment = segments.findLast(item => seconds + 1e-9 >= item.transitionStartSeconds);
    const amount = segment ? cinematicTransitionAmount(segment, seconds) : 1;
    const evaluated = evaluateProperties(bounded);
    return { ...evaluated, mapStyle: evaluated.settings.mapStyle, segment, amount };
  } };
}
